import { useCallback, useEffect, useState } from 'react';

type NavigationItem = { hash?: string };

export const useWindowHashNavigation = <T extends NavigationItem>(
  navigationItems: T[],
  removeHashOnFirstElement = true,
) => {
  const [activeItem, setActiveItem] = useState<number>(
    findNavigationItemIndexForCurrentHash(navigationItems),
  );

  const handleNavigationChange = useCallback(
    (newIndex: number) => {
      window.location.hash = navigationItems[newIndex]?.hash || '';
    },
    [navigationItems],
  );

  const handleHashChange = useCallback(() => {
    const newIdx = findNavigationItemIndexForCurrentHash(navigationItems);

    if (newIdx === 0) {
      if (removeHashOnFirstElement) {
        // Selecting the first tab will set the hash to an empty string. This will
        // not remove the '#' from the URL. That's why we manually remove the '#'
        // using a `replaceState` call. This removes the '#' without triggering a
        // page refresh.
        window.history.replaceState(null, '', ' ');
      }
    }

    setActiveItem(newIdx);
  }, [navigationItems, removeHashOnFirstElement]);

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [navigationItems, handleHashChange]);

  return { activeItem, handleNavigationChange };
};

const findNavigationItemIndexForCurrentHash = <T extends NavigationItem>(
  navigationItems: T[],
): number => {
  const targetIndex = navigationItems.findIndex((t) =>
    // Check if the object "window" is available, because during the gatsby build window is not defined
    typeof window !== 'undefined' ? t?.hash === window.location.hash : true,
  );

  if (targetIndex < 0) {
    return 0;
  }

  return targetIndex;
};
